import { sceneHelpTemplate } from 'models/copilot/actions/aiActionTemplates';
import { CopilotAction, CopilotActionType } from 'models/copilot/copilotAction';
import { CopilotConversation } from 'models/copilot/copilotConversation';
import { CopilotPanelProcessState } from 'models/copilot/copilotPanelContextDefinition';
import { TenantProcess, TenantProcessStatus } from 'models/copilot/tenantProcessStateBase';

export type CopilotPanelActiveTarget =
  | { kind: 'none' }
  | { kind: 'process'; processId: TenantProcess }
  | { kind: 'session'; sessionId?: number };

export type CopilotPanelProcessStatesById = Partial<Record<TenantProcess, CopilotPanelProcessState>>;

export const hasConversationContent = (conversation: CopilotConversation | undefined): boolean =>
  conversation?.thread?.some((item) => !!item.response || (item.contentBlocks?.length ?? 0) > 0) === true;

export const ensureDocxFileName = (title: string | undefined, fallback: string): string => {
  const sanitized = (title || fallback).replace(/[<>:"/\\|?*]+/g, ' ').trim() || fallback;

  return sanitized.toLowerCase().endsWith('.docx') ? sanitized : `${sanitized}.docx`;
};

const isJsonRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const tryParseJsonRecord = (value: unknown): Record<string, unknown> | undefined => {
  if (isJsonRecord(value)) return value;
  if (typeof value !== 'string') return undefined;

  try {
    const parsed: unknown = JSON.parse(value);

    return isJsonRecord(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
};

const hasAITaskApprovalSubmitAction = (adaptiveCardJson: unknown): boolean => {
  const card = tryParseJsonRecord(adaptiveCardJson);
  const actions = card?.actions;
  if (!Array.isArray(actions)) return false;

  return actions.some((action) => {
    if (!isJsonRecord(action)) return false;
    const data = tryParseJsonRecord(action.data);

    return data?.action === 'aiTaskApproval';
  });
};

export const hasPendingAITaskApproval = (conversation: CopilotConversation | undefined): boolean => {
  const latestItem = conversation?.thread?.[conversation.thread.length - 1];

  return latestItem?.contentBlocks?.some((block) => hasAITaskApprovalSubmitAction(block.adaptiveCardJson)) === true;
};

export const getProcessActions = (actions: CopilotAction[]): CopilotAction[] =>
  actions.filter((action) => action.type === CopilotActionType.Process && action.processId !== undefined);

/**
 * The panel's order of a usage's actions, for the action list and the composer's Actions menu alike: the help
 * question first, then the other questions by label, then the processes by label.
 */
export const orderCopilotActions = (actions: CopilotAction[]): CopilotAction[] => {
  const byName = (left: CopilotAction, right: CopilotAction) => left.name.localeCompare(right.name);
  const isHelp = (action: CopilotAction) => action.id === sceneHelpTemplate.id;
  const isProcess = (action: CopilotAction) => action.type === CopilotActionType.Process;

  return [
    ...actions.filter(isHelp),
    ...actions.filter((action) => !isHelp(action) && !isProcess(action)).sort(byName),
    ...actions.filter((action) => !isHelp(action) && isProcess(action)).sort(byName),
  ];
};

/**
 * The action a run belongs to. A caller that starts a process hands in that one action, so it wins:
 * it carries the status of the run that is starting, where the same action in panel state can still
 * carry the status of the previous run - and a definition decides from that status whether it starts
 * a proposal or commits one. Any other run keeps the process the panel has selected.
 */
export const resolveRuntimeAction = (
  runtimeActions: CopilotAction[],
  activeAction: CopilotAction | undefined,
): CopilotAction | undefined => {
  if (runtimeActions.length === 1) return runtimeActions[0];

  return (
    activeAction ??
    runtimeActions.find((action) => action.status === TenantProcessStatus.InProgress) ??
    // the first process, not the help prompt the ordered list starts with
    getProcessActions(runtimeActions)[0] ??
    runtimeActions[0]
  );
};

const isFreshStartableProcessAction = (action: CopilotAction, statesById: CopilotPanelProcessStatesById): boolean => {
  if (action.processId === undefined) return false;
  if (statesById[action.processId]) return false;

  return action.status === TenantProcessStatus.NotStarted || action.status === TenantProcessStatus.Cancelled;
};

export const areAllProcessActionsFreshStartable = (
  processActions: CopilotAction[],
  statesById: CopilotPanelProcessStatesById,
): boolean =>
  processActions.length > 0 && processActions.every((action) => isFreshStartableProcessAction(action, statesById));

export const getLatestRunningProcessState = (
  states: CopilotPanelProcessState[],
): CopilotPanelProcessState | undefined => {
  return states
    .filter((state) => state.status === TenantProcessStatus.InProgress)
    .sort((left, right) => (right.modified?.getTime() ?? 0) - (left.modified?.getTime() ?? 0))[0];
};
